import React, { useEffect, useState, useRef } from 'react'
import TransitionWrapper from '../atoms/TransitionWrapper'
import Line from '../atoms/HorizontalLine'
import Button from '../atoms/Button'
import ImageCard from '../atoms/ImageCard'
import { getUserProfile } from '../../utils/API/getUserProfileAPI'
import { updateUserProfile } from '../../utils/API/updateUserProfileApi'
import { uploadDocument } from '../../utils/API/uploadDocuments'
import { Country } from 'country-state-city'
import { Skeleton } from 'antd'
import toast from 'react-hot-toast'
import EditIcon from '../svgs/edit.png'
import { useDispatch } from 'react-redux'

const ProfilePage = () => {
  const dispatch = useDispatch()
  const [userData, setUserData] = useState(null)
  const [editUserData, setEditUserData] = useState({
    contact_number: { phone_no: '', country_code: '' },
    location: '',
    description: ''
  })
  const [countries, setCountries] = useState([])
  const [countryCodes, setCountryCodes] = useState({})
  const [loading, setLoading] = useState(true)
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false)
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const descriptionRef = useRef(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getUserProfile(dispatch)
        if (response.success) {
          setUserData(response.data)
          setEditUserData({
            contact_number: response.data.contact_number,
            location: response.data.location,
            description: response.data.description
          })
        } else {
          console.error('Failed to fetch user profile')
        }
      } catch (error) {
        console.error('Error fetching user profile:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()

    const countriesData = Country.getAllCountries()
    setCountries(countriesData.map(country => country.name))

    const countryCodesMap = {}
    countriesData.forEach(country => {
      countryCodesMap[country.name] = country.phonecode
    })
    setCountryCodes(countryCodesMap)
  }, [])

  const handleSaveClick = async () => {
    try {
      setSavingProfile(true)
      const response = await updateUserProfile(editUserData, dispatch)
      if (response.success) {
        setUserData(response.data)
        setEditMode(false)
        toast.success('Profile updated successfully!')
      } else {
        console.error('Failed to update user profile')
        toast.error('Failed to update profile. Please try again.')
      }
    } catch (error) {
      console.error('Error updating user profile:', error)
      toast.error(
        'An error occurred while updating profile. Please try again later.'
      )
    } finally {
      setSavingProfile(false)
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    if (name === 'location') {
      const countryCode = countryCodes[value] || ''
      const formattedCountryCode = countryCode.startsWith('+')
        ? countryCode
        : `+${countryCode}`
      setEditUserData(prevData => ({
        ...prevData,
        [name]: value,
        contact_number: {
          ...prevData.contact_number,
          country_code: formattedCountryCode
        }
      }))
    } else if (name === 'phone_no') {
      if (/^\d+$/.test(value) || value === '') {
        setEditUserData(prevData => ({
          ...prevData,
          contact_number: {
            ...prevData.contact_number,
            [name]: value
          }
        }))
      }
    } else {
      setEditUserData(prevData => ({
        ...prevData,
        [name]: value
      }))
    }
  }

  const handleChangeImage = async (e, type) => {
    try {
      const file = e.target.files[0]

      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB')
        return
      }

      if (type === 'profile') {
        setUploadingProfileImage(true)
      } else if (type === 'cover') {
        setUploadingCoverImage(true)
      }

      const fieldName = type === 'profile' ? 'profile_image' : 'cover_image'
      const response = await uploadDocument(file, fieldName, 1, 1, dispatch)
      if (response.success) {
        const imageURL = response.data

        const imageData = {
          [fieldName]: `${imageURL}?timestamp=${new Date().getTime()}`
        }

        const updateResponse = await updateUserProfile(imageData, dispatch)

        if (updateResponse.success) {
          setUserData(prevUserData => ({
            ...prevUserData,
            [fieldName]: `${imageURL}?timestamp=${new Date().getTime()}`
          }))
        } else {
          console.error(`Failed to update user profile with ${type} image data`)
        }
      } else {
        console.error(`Failed to upload ${type} image`)
      }
    } catch (error) {
      console.error(`Error uploading ${type} image:`, error)
    } finally {
      if (type === 'profile') {
        setUploadingProfileImage(false)
      } else if (type === 'cover') {
        setUploadingCoverImage(false)
      }
      if (e.target.value) {
        e.target.value = ''
      }
    }
  }

  const handleEditIconClick = () => {
    setEditMode(true)
    if (descriptionRef.current) {
      descriptionRef.current.focus()
    }
  }

  if (loading) {
    return (
      <div className='flex flex-col gap-y-4 w-full'>
        <Skeleton.Button block={true} active={true} />
        <Skeleton.Button block={true} active={true} />
        <Skeleton.Button block={true} active={true} />
        <Skeleton.Button block={true} active={true} />
      </div>
    )
  }

  return (
    <TransitionWrapper>
      <div className='bg-gray-100 w-full h-full p-6 relative capitalize'>
        <div className='bg-white px-4 py-4 rounded h-[70rem] relative'>
          <div className='relative pr-1'>
            <ImageCard
              iconDisplay='flex'
              classnames='w-[full] h-[13rem] bg-cover rounded-xl relative z-auto'
              userImage={userData?.cover_image}
            />
            {uploadingCoverImage && (
              <div className='absolute inset-0 flex items-center justify-center bg-gray-300 bg-opacity-50'>
                <div className='w-1/4'>
                  Uploading Cover Image...
                  <Skeleton.Button block={true} active={true} />
                </div>
              </div>
            )}
            <Button
              varient='primary'
              borderColor='none'
              className='absolute bottom-4 right-4 text-sm text-blue-800 font-medium bg-blue-300 px-4 py-1'
            >
              <input
                type='file'
                accept='image/png, image/jpeg, image/jpg'
                className='absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer'
                onChange={e => handleChangeImage(e, 'cover')}
              />
              + Change Cover Image
            </Button>
          </div>
          <div className='flex'>
            {' '}
            <div className='rounded-full absolute mt-[-1.5rem] h-28 w-28 ml-6 flex items-center justify-center'>
              <ImageCard
                iconDisplay='flex'
                classnames='w-full h-full bg-cover rounded-full'
                userImage={userData?.profile_image}
              />
              {uploadingProfileImage && (
                <div className='absolute inset-0 flex items-center justify-center bg-gray-300 bg-opacity-50 rounded-full'>
                  <div className='loader'>Loading...</div>
                </div>
              )}
              <Button
                varient='rounded'
                borderColor='none'
                className='absolute bottom-1 left-20 right-0.5 text-3xl text-white font-light bg-blue-800 px-1.5 w-8 h-8 flex items-center justify-center' // Added flex properties to center the content
              >
                <input
                  type='file'
                  accept='image/png, image/jpeg, image/jpg'
                  className='absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer'
                  onChange={e => handleChangeImage(e, 'profile')}
                />
                +
              </Button>
            </div>
            <img
              src={EditIcon}
              alt='Edit Icon'
              className='w-6 h-6 ml-2 mt-4 cursor-pointer absolute right-6'
              onClick={handleEditIconClick}
            />
          </div>

          <div>
            <div className='mb-5 ml-[10rem] pt-3 flex flex-col'>
              <div className='text-4xl'>{userData?.name}</div>
              <div className='text-2xl text-grey-500 font-bold'>
                {userData?.user_type}
              </div>
            </div>
          </div>
          <div className='relative pl-8'>
            <div className='flex items-center text-2xl font-medium'>
              Basic information
            </div>
            <div className=' w-3/4 '>
              <div className='flex gap-5'>
                <div className=' w-1/2 my-4 '>
                  <p className='pb-3 text-sm font-normal'>Name</p>
                  <p className='text-base font-normal'>{userData?.name}</p>
                  <Line className='' />
                </div>
                <div className='w-1/2  my-4'>
                  <p className='pb-3 text-sm '>User Type</p>
                  <p className='text-base font-normal'>{userData?.user_type}</p>
                  <Line className='' />
                </div>
              </div>
              <div className='flex gap-5'>
                <div className=' w-full my-4 '>
                  <p className='pb-3 text-sm font-normal'>User ID</p>
                  <p className='text-base font-normal'>{userData?.user_id}</p>
                  <Line className='' />
                </div>
                {/* <div className="w-1/2  my-4"> */}
                <div className='w-full my-4 relative'>
                  <p className='pb-3 text-sm'>Location</p>
                  <div className='relative'>
                    <select
                      name='location'
                      className='outline-none focus:ring-0 focus:border-transparent bg-white w-full'
                      value={editUserData.location}
                      onChange={handleChange}
                      disabled={!editMode}
                    >
                      <option value=''>Select a country</option>
                      {countries.map((country, index) => (
                        <option key={index} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                      <div className='w-5 h-5 bg-white'></div>
                    </div>
                  </div>
                  <Line className='' />
                </div>
                {/* </div> */}
              </div>

              <div className=' mb-4 gap-5 flex'>
                <div className='w-1/2'>
                  <p className='pb-3 text-sm '>Email-ID </p>
                  <p className='font-normal normal-case'>{userData?.email_id}</p>
                  <Line className='' />
                </div>
                <div className='w-1/2'>
                  <p className='pb-3 text-sm '>Contact Number</p>
                  <div className='flex'>
                    <div className='w-7 '>
                      <input
                        type='text'
                        name='country_code'
                        className=' outline-none focus:ring-0 focus:border-transparent'
                        value={editUserData.contact_number.country_code}
                        onChange={handleChange}
                        readOnly={!editMode}
                      />
                    </div>

                    <span className='mx-2'> </span>
                    <div>
                      <input
                        type='text'
                        name='phone_no'
                        className='outline-none focus:ring-0 focus:border-transparent'
                        value={editUserData.contact_number.phone_no}
                        onChange={handleChange}
                        readOnly={!editMode}
                      />
                    </div>
                  </div>
                  <Line className='' />
                </div>
              </div>
              {/* <div className="w-1/5 relative">
                <p className="pb-3 text-sm">Location</p>
                <div className="relative">
                  <select
                    name="location"
                    className="outline-none focus:ring-0 focus:border-transparent bg-white w-full"
                    value={editUserData.location}
                    onChange={handleChange}
                    disabled={!editMode}
                  >
                    <option value="">Select a country</option>
                    {countries.map((country, index) => (
                      <option key={index} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="w-5 h-5 bg-white"></div>
                  </div>
                </div>
                <Line className="" />
              </div> */}

              <div className='my-4'>
                <label className='block text-sm mb-4 font-medium text-gray-700'>
                  Description
                </label>
                <textarea
                  ref={descriptionRef}
                  rows='3'
                  name='description'
                  value={editUserData.description}
                  onChange={handleChange}
                  className={`mt-1 p-2 block w-full h-30 border border-gray-400 rounded-md focus:outline-none focus:border-gray-800`}
                  disabled={!editMode}
                ></textarea>
              </div>
              <div className='flex gap-5'>
                <div className=' w-1/3 my-4 '>
                  <p className='pb-3 text-sm font-normal'>
                    No. Of Transactions
                  </p>
                  <p className='text-base font-normal'>
                    {userData?.no_of_transactions}
                  </p>
                  <Line className='' />
                </div>
                <div className='w-1/3  my-4'>
                  <p className='pb-3 text-sm '>Amount Of Transactions</p>
                  <p className='text-base font-normal'>
                    {userData?.amount_of_transactions}
                  </p>
                  <Line className='' />
                </div>
              </div>
              <div className='w-2/3 mt-4 '>
                <p className='text-sm '>Authenticity</p>

                <div className='flex justify-end'>
                  <div className='text-blue-800 text-base'>
                    {userData ? `${userData.authenticity ?? 0}%` : 'Loading...'}
                  </div>
                </div>
                <div className='w-full bg-grey-300 rounded-full '>
                  <div
                    className='h-1 rounded-full bg-blue-800'
                    style={{
                      width: `${userData ? userData.authenticity ?? 0 : 0}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {editMode && (
            <div className='ml-5 mt-12'>
              <Button
                varient='primary'
                borderColor='blue'
                className='w-3/4 px-9 py-1 text-sm justify-right lg:py-2 text-white font-bold bg-blue-800'
                onClick={handleSaveClick}
              >
                <div className='flex justify-center'>
                  {savingProfile ? <div>Loading...</div> : <div>Save</div>}
                </div>
              </Button>
            </div>
          )}
        </div>
      </div>
    </TransitionWrapper>
  )
}

export default ProfilePage
